import { FC, useMemo, useState } from 'react';
import cn from 'classnames';

import { isAttachmentDeleted } from '@/entities/vfo/helpers';
import { useNotices } from '@/features/notices/useNotices';
import { Avatar } from '@/features/vfo/components';
import { useDocuments } from '@/features/vfo/hooks/useDocuments';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { ApiCaseResponseV2, ApiMessageResponse } from '@/shared/api';
import { FileTypeIcon } from '@/shared/components/FileTypeIcon';
import { COLORS } from '@/shared/constants/colors';
import { getPersonFullName } from '@/shared/helpers/names';
import { useAuthStore } from '@/shared/store/auth.store';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';
import { Spinner } from '@/shared/uikitV2/Spinner';
import { dates } from '@/shared/utils/dates';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { numbers } from '@/shared/utils/numbers';
import { useModalsStore } from '@/widgets/modals/modals.store';

import { mimeTypeToFileInfoFileType } from '../../FilePreviewModal/FilePreview/helpers';

import { ReplyContent } from './ReplyContent';
import {
  Attachment,
  ButtonUserName,
  FileContent,
  FileTitle,
  FileTypeWrapper,
  MessageActions,
  MessageContent,
  MessageItem,
  SystemMessage,
  Timestamp,
} from './styled';

type MessageListProps = {
  messages: ApiMessageResponse[];
  caseData?: ApiCaseResponseV2;
  scrollToMessage: (messageId: string) => void;
  isCaseDeleted?: boolean;
  setReplyMessage: (data: {
    id: string;
    text: string;
    user: {
      id: number;
      firstName?: string;
      lastName?: string;
    };
  }) => void;
};

export const MessageList: FC<MessageListProps> = ({
  messages,
  caseData,
  scrollToMessage,
  isCaseDeleted,
  setReplyMessage,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'vfoChat' });

  const { downloadDocument, getDocumentsByCaseId, documents } = useDocuments();
  const { getByProfileId } = useMembers();
  const { account } = useAuthStore();
  const notices = useNotices();
  const modals = useModalsStore();

  const [attachmentLoadings, setAttachmentLoadings] = useState<Record<string, boolean>>({});
  const currentProfileId = account?.id;

  const caseDocuments = useMemo(() => {
    if (!caseData) return null;
    return getDocumentsByCaseId(caseData.id);
  }, [caseData]);

  return messages.map((item) => {
    if (item.isSystem) {
      return <SystemMessage key={item.id}>{item.body ?? ''}</SystemMessage>;
    }

    const isMyMessage = item.profileId === currentProfileId;
    const member = getByProfileId(item.profileId);
    if (!member) return null;
    return (
      <MessageItem
        className={cn({ me: isMyMessage })}
        key={item.id}
        data-messageId={item.id}
        onClick={() => {
          if (item.body && window.matchMedia(`(max-width: 800px)`).matches) {
            modals.openModal({
              type: 'MessageActionsModal',
              onClick: (action) => {
                if (action === 'reply') {
                  setReplyMessage({
                    id: item.id,
                    text: item.body ?? '',
                    user: {
                      id: item.profileId,
                      firstName: member.firstName,
                      lastName: member.lastName,
                    },
                  });
                } else if (caseData && action === 'createTask') {
                  modals.openModal({
                    type: 'AddNewTaskModal',
                    caseId: caseData.id,
                    text: item.body ?? '',
                    messageId: item.id,
                  });
                }
              },
            });
          }
        }}
      >
        <Avatar
          member={member}
          size="s36"
          showStatus
          onClick={() => {
            modals.openModal({
              type: 'VfoProfileModal',
              initialMember: member,
              onOpenCase: modals.closeModal,
            });
          }}
        />
        <MessageContent className={cn({ me: isMyMessage })}>
          <ButtonUserName
            type="text"
            className={cn({ me: isMyMessage })}
            size="small"
            onClick={() => {
              modals.openModal({
                type: 'VfoProfileModal',
                initialMember: member,
              });
            }}
          >
            {getPersonFullName(member, 'display')}
          </ButtonUserName>
          {item.replyToMessageId && (
            <ReplyContent
              onClick={() => item.replyToMessageId && scrollToMessage(item.replyToMessageId)}
              messages={messages}
              messageId={item.replyToMessageId}
            />
          )}
          {item.attachments.map((file, index) => {
            const isDeleted = isAttachmentDeleted(file);

            const isInDocuments =
              !isDeleted && documents.some((doc) => doc.filePath === file.filePath);
            const isInCase =
              !isDeleted &&
              (!caseDocuments || caseDocuments.some((doc) => doc.filePath === file.filePath));

            const isAvailable = isInDocuments && isInCase;

            if (isDeleted || !isAvailable) {
              return (
                <Attachment key={index} className="nonClickable">
                  <FileTypeWrapper className="deleted">
                    <Icon name="file-x" size="s20" />
                  </FileTypeWrapper>
                  <FileContent>
                    <FileTitle className="fullWidth">
                      {isDeleted
                        ? t('noAttachment')
                        : !isInDocuments
                        ? t('noAttachmentInVfo')
                        : t('noAttachmentInCase')}
                    </FileTitle>
                  </FileContent>
                </Attachment>
              );
            }

            return (
              <Attachment
                key={file.id}
                onClick={async () => {
                  if (attachmentLoadings[file.id]) return;
                  setAttachmentLoadings((prev) => ({
                    ...prev,
                    [file.id]: true,
                  }));

                  const response = await downloadDocument(file.filePath);
                  if (!response) return;
                  const type = mimeTypeToFileInfoFileType(file.mimeType);

                  setAttachmentLoadings((prev) => ({
                    ...prev,
                    [file.id]: false,
                  }));
                  if (!type) {
                    notices.error("Can't open file");
                    return;
                  }

                  modals.openModal({
                    type: 'FilePreviewModal',
                    fileInfo: {
                      name: file.title ?? 'File',
                      fileType: type,
                      size: Number(file.fileSizeBytes),
                      url: response.fileContent,
                    },
                    mode: 'fileInfo',
                  });
                }}
              >
                <FileTypeWrapper className={cn({ loading: attachmentLoadings[file.id] })}>
                  {attachmentLoadings[file.id] ? (
                    <Spinner />
                  ) : (
                    <FileTypeIcon type={file.mimeType} />
                  )}
                </FileTypeWrapper>
                <FileContent>
                  <FileTitle>{file.title}</FileTitle>
                  <span>{numbers.formatUnits(file.fileSizeBytes, 'byte')}</span>
                </FileContent>
              </Attachment>
            );
          })}
          <span>{item.body ?? ''}</span>
          <Timestamp>{dates.formatTime(item.createdAt)}</Timestamp>

          {!isCaseDeleted && (
            <MessageActions className="actions">
              <Button
                size="small"
                type="default"
                shape="round"
                icon={<Icon name="plus" size="s20" />}
                style={{
                  color: COLORS.systemSky200,
                }}
                onClick={() => {
                  const attachments = documents.filter((doc) =>
                    item.attachments.some(
                      (file) => !isAttachmentDeleted(file) && file.filePath === doc.filePath,
                    ),
                  );

                  if (!caseData) return;
                  modals.openModal({
                    type: 'AddNewTaskModal',
                    caseId: caseData.id,
                    text: item.body ?? '',
                    messageId: item.id,
                    initialAttachments: attachments,
                  });
                }}
              >
                {t('createTask')}
              </Button>
              <Button
                size="small"
                type="default"
                shape="round"
                icon={<Icon name="arrow-bend-up-left" size="s20" />}
                onClick={() => {
                  if (!caseData) return;
                  setReplyMessage({
                    id: item.id,
                    text: item.body ?? '',
                    user: {
                      id: item.profileId,
                      firstName: member.firstName,
                      lastName: member.lastName,
                    },
                  });
                }}
              >
                {t('reply')}
              </Button>
            </MessageActions>
          )}
        </MessageContent>
      </MessageItem>
    );
  });
};
